"use client";

import { useState, useTransition } from "react";
import { useRouter } from "next/navigation";
import { AlertCircle, Check, FileText, Send, Users, WifiOff } from "lucide-react";

import { getWhatsAppPoDraft, confirmWhatsAppPoSent, getWhatsAppGroupPreview, sendPoToWhatsAppGroup, type WhatsAppGroupPreview } from "../approvals/actions";
import {
  COPY,
  groupCaptionLine,
  groupAlreadyInGroupTitle,
  groupReplyingTo,
  groupTagsContact,
  groupUnsureLine,
  sendToGroupLabel,
  sentToGroupLabel,
} from "../approvals/copy";
import type { PoGroupSendView } from "../approvals/view";
import { describeTransportError } from "../_lib/client-errors";
import { pick, type SupplyLang } from "../_lib/lang";

const GREEN =
  "flex h-[44px] w-full items-center justify-center gap-2 rounded-[12px] bg-[#25D366] px-3 text-[14px] font-bold text-[#04310F] transition active:scale-[0.985] disabled:opacity-60";
const QUIET =
  "flex h-[44px] w-full items-center justify-center rounded-[12px] border border-[var(--border)] bg-[var(--surface)] px-3 text-[14px] font-bold text-[var(--body)] transition active:scale-[0.985] disabled:opacity-60";
const MANUAL_LINK =
  "mt-2 flex min-h-11 w-full items-center justify-center text-[13px] font-bold text-[var(--muted)] underline decoration-dotted underline-offset-2";

/**
 * Send the PO into the supplier's WhatsApp group from the ops number (Shaq,
 * 2026-09-29). Design: docs/design/2026-09-29-whatsapp-po-send-mockup.html.
 * Always asks once (preview) before sending: a message to a supplier cannot be
 * taken back. The share-it-yourself controls stay one tap away.
 */
export function WhatsAppGroupSend({
  orderId,
  group,
  lang,
  onResult,
  manualOpen,
  onOpenManual,
}: {
  orderId: string;
  group: PoGroupSendView;
  lang: SupplyLang;
  onResult: (message: string) => void;
  manualOpen: boolean;
  onOpenManual: () => void;
}) {
  const router = useRouter();
  const [preview, setPreview] = useState<WhatsAppGroupPreview | null>(null);
  const [confirmNotInGroup, setConfirmNotInGroup] = useState(false);
  const [confirmedFor, setConfirmedFor] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [isPending, startTransition] = useTransition();

  async function openPreview(confirmed = false) {
    setConfirmNotInGroup(confirmed);
    setConfirmedFor(confirmed ? group.confirmFor : null);
    setLoading(true);
    try {
      const result = await getWhatsAppGroupPreview({ orderId });
      if (result.ok) setPreview(result.preview);
      else onResult(result.error);
    } catch (error) {
      const friendly = describeTransportError(error);
      if (friendly === null) throw error;
      onResult(friendly);
    } finally {
      setLoading(false);
    }
  }

  function sendNow() {
    if (!preview) return;
    const contentKey = preview.contentKey;
    startTransition(async () => {
      try {
        const result = await sendPoToWhatsAppGroup({ orderId, contentKey, ...(confirmNotInGroup ? { confirmedNotInGroup: true, confirmedFor: confirmedFor ?? undefined } : {}) });
        if (result.ok) onResult(pick(lang, result.noteMissed ? COPY.groupSentNoteMissedToast : COPY.groupSentToast));
        else onResult(result.error ?? pick(lang, COPY.couldNotSendTryAgain));
        if (confirmNotInGroup || result.ok || result.contentChanged || result.notSure) setPreview(null);
        router.refresh();
      } catch (error) {
        const friendly = describeTransportError(error);
        if (friendly === null) throw error;
        onResult(friendly);
      }
    });
  }

  function markAsSent() {
    startTransition(async () => {
      const d = await getWhatsAppPoDraft({ orderId });
      if (!d.ok) { onResult(d.error); return; }
      const r = await confirmWhatsAppPoSent({ orderId, contentKey: d.draft.contentKey });
      onResult(r.ok ? pick(lang, COPY.recordedSentWhatsapp) : (r.error ?? pick(lang, COPY.couldNotRecordIt)));
      router.refresh();
    });
  }

  if (group.mode === "already_posted") {
    return (
      <div className="flex flex-col gap-2">
        <div className="flex items-start gap-2 rounded-[12px] bg-[var(--success-well)] px-3 py-2.5">
          <span className="mt-0.5 grid size-5 shrink-0 place-items-center rounded-full bg-[var(--success)] text-[var(--inner)]">
            <Check className="size-3.5" strokeWidth={2.6} aria-hidden />
          </span>
          <span className="min-w-0">
            <span className="block text-[14px] font-bold text-[var(--success)]">{groupAlreadyInGroupTitle(group.label, lang)}</span>
            {group.postedLine ? <span className="block text-[12px] font-semibold text-[var(--body)]">{group.postedLine}</span> : null}
          </span>
        </div>
        <button type="button" onClick={markAsSent} disabled={isPending} className={QUIET}>
          {pick(lang, COPY.groupMarkAsSent)}
        </button>
      </div>
    );
  }

  if (group.mode === "sent") {
    return (
      <div className="flex items-start gap-2 rounded-[12px] bg-[var(--success-well)] px-3 py-2.5">
        <span className="mt-0.5 grid size-5 shrink-0 place-items-center rounded-full bg-[var(--success)] text-[var(--inner)]">
          <Check className="size-3.5" strokeWidth={2.6} aria-hidden />
        </span>
        <span className="min-w-0">
          <span className="block text-[14px] font-bold text-[var(--success)]">{sentToGroupLabel(group.label, lang)}</span>
          {group.sentLine ? <span className="block text-[12px] font-semibold text-[var(--body)]">{group.sentLine}</span> : null}
          <span className="mt-1 block text-[12px] font-semibold text-[var(--muted)]">
            {pick(lang, group.sentKind === "by_hand" ? COPY.groupRecordedByHand : group.sentKind === "note_missed" ? COPY.groupNoteMissed : COPY.groupPosted)}
          </span>
        </span>
      </div>
    );
  }

  if (group.mode === "offline") {
    return (
      <div className="flex items-start gap-2 rounded-[12px] bg-[var(--warning-well)] px-3 py-2.5">
        <WifiOff className="mt-0.5 size-[18px] shrink-0 text-[var(--warning)]" strokeWidth={2.2} aria-hidden />
        <span className="min-w-0">
          <span className="block text-[14px] font-bold text-[var(--warning)]">{pick(lang, COPY.groupOfflineTitle)}</span>
          <span className="block text-[12px] font-semibold text-[var(--body)]">{pick(lang, COPY.groupOfflineLine)}</span>
        </span>
      </div>
    );
  }

  // 'sending' stays pressable: the server answers in_progress while the claim is fresh and
  // heals it after 10 minutes. A disabled button here would strand the PO for good.
  const busy = loading || isPending;
  const uncertain = group.mode === "failed" && group.failKind === "uncertain";

  return (
    <div className="flex flex-col">
      <div className="mb-2 flex min-w-0 items-center gap-2">
        <span className="grid size-8 shrink-0 place-items-center rounded-full bg-[var(--success-well)] text-[var(--success)]">
          <Users className="size-4" strokeWidth={2.2} aria-hidden />
        </span>
        <span className="min-w-0">
          <span className="block truncate text-[13px] font-bold text-[var(--text)]">{group.label}</span>
          <span className="block text-[11px] font-semibold text-[var(--muted)]">{pick(lang, COPY.groupCaption)}</span>
        </span>
      </div>

      {group.mode === "sending" && preview === null ? (
        <p className="mb-2 rounded-[12px] bg-[var(--info-well)] px-3 py-2.5 text-[12px] font-semibold text-[var(--info)]">
          {pick(lang, COPY.groupStillSending)}
        </p>
      ) : null}

      {uncertain && preview === null ? (
        <div className="mb-2 flex items-start gap-2 rounded-[12px] bg-[var(--warning-well)] px-3 py-2.5">
          <AlertCircle className="mt-0.5 size-[18px] shrink-0 text-[var(--warning)]" strokeWidth={2.2} aria-hidden />
          <span className="min-w-0">
            <span className="block text-[14px] font-bold text-[var(--warning)]">{pick(lang, COPY.groupUnsureTitle)}</span>
            <span className="block text-[12px] font-semibold text-[var(--body)]">{groupUnsureLine(group.label, lang)}</span>
          </span>
        </div>
      ) : group.mode === "failed" && preview === null ? (
        <div className="mb-2 flex items-start gap-2 rounded-[12px] bg-[var(--danger-well)] px-3 py-2.5">
          <AlertCircle className="mt-0.5 size-[18px] shrink-0 text-[var(--danger)]" strokeWidth={2.2} aria-hidden />
          <span className="min-w-0">
            <span className="block text-[14px] font-bold text-[var(--danger)]">{pick(lang, COPY.groupNotSentTitle)}</span>
            <span className="block text-[12px] font-semibold text-[var(--body)]">{pick(lang, group.failKind === "not_posted" ? COPY.groupNotPostedLine : COPY.groupNotSentLine)}</span>
          </span>
        </div>
      ) : null}

      {preview !== null ? (
        <>
          <div className="rounded-[12px] border border-[var(--border-soft)] bg-[var(--surface)] p-2.5">
            <p className="mb-2 text-[11px] font-bold uppercase tracking-[0.08em] text-[var(--muted)]">{pick(lang, COPY.groupWillGet)}</p>
            <div className="flex items-center gap-2 rounded-[10px] bg-[var(--raised)] px-2.5 py-2">
              <FileText className="size-[18px] shrink-0 text-[var(--danger)]" strokeWidth={2.2} aria-hidden />
              <span className="min-w-0">
                <span className="block truncate text-[13px] font-bold text-[var(--text)]">{preview.fileName}</span>
                <span className="block text-[11px] font-semibold text-[var(--muted)]">{groupCaptionLine(preview.caption, lang)}</span>
              </span>
            </div>
            <div className="mt-1.5 rounded-[10px] bg-[var(--raised)] px-2.5 py-2">
              <p className="rounded-[8px] bg-[var(--success-well)] px-2 py-1 text-[11px] font-semibold text-[var(--muted)]">
                {groupReplyingTo(preview.fileName, lang)}
              </p>
              <p className="mt-1 whitespace-pre-line text-[13px] font-medium leading-[1.45] text-[var(--text)]">
                <WhatsAppText text={preview.text} tag={preview.tagDisplay} />
              </p>
            </div>
            <p className="mt-2 text-[11px] font-semibold text-[var(--muted)]">
              {preview.tagDisplay ? groupTagsContact(preview.tagDisplay, lang) : pick(lang, COPY.groupNoContact)}
            </p>
          </div>
          <div className="mt-2 grid grid-cols-2 gap-2">
            <button type="button" onClick={() => setPreview(null)} disabled={isPending} className={QUIET}>
              {pick(lang, COPY.groupBack)}
            </button>
            <button type="button" onClick={sendNow} disabled={isPending} className={GREEN}>
              {isPending ? pick(lang, COPY.groupSendingButton) : pick(lang, COPY.groupSendNow)}
            </button>
          </div>
        </>
      ) : (
        <button type="button" onClick={() => openPreview(uncertain)} disabled={busy} className={uncertain ? QUIET : GREEN}>
          {!uncertain ? <Send className="size-4" strokeWidth={2.2} aria-hidden /> : null}
          {isPending
            ? pick(lang, COPY.groupSendingButton)
            : loading
              ? pick(lang, COPY.preparingPoButton)
              : uncertain
                ? pick(lang, COPY.groupNotThereSendAgain)
                : group.mode === "failed" || group.mode === "sending"
                  ? pick(lang, COPY.groupTryAgain)
                  : sendToGroupLabel(group.label, lang)}
        </button>
      )}

      {uncertain && preview === null ? (
        <button type="button" onClick={markAsSent} disabled={busy} className={QUIET}>
          {pick(lang, COPY.groupMarkAsSent)}
        </button>
      ) : null}

      {!manualOpen ? (
        <button type="button" onClick={onOpenManual} className={MANUAL_LINK}>
          {pick(lang, COPY.shareYourselfInstead)}
        </button>
      ) : null}
    </div>
  );
}

/** WhatsApp's *bold* and the tag, rendered as the group will see them. */
function WhatsAppText({ text, tag }: { text: string; tag: string | null }) {
  const token = tag ? `@${tag}` : null;
  return (
    <>
      {text.split(/(\*[^*\n]+\*)/).map((part, i) => {
        if (/^\*[^*\n]+\*$/.test(part)) return <b key={i}>{part.slice(1, -1)}</b>;
        if (token && part.includes(token)) {
          const [before, ...rest] = part.split(token);
          return (
            <span key={i}>
              {before}
              <span className="font-bold text-[var(--info)]">{token}</span>
              {rest.join(token)}
            </span>
          );
        }
        return <span key={i}>{part}</span>;
      })}
    </>
  );
}
